import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin } from "lucide-react";
import { fetchLocationSuggestions, type LocationSuggestion } from "@/lib/location-autocomplete";

/**
 * Single-input city / ZIP location autocomplete.
 * - 5-digit ZIP → resolves to "City, ST" via Zippopotam.us
 * - Otherwise queries Photon for cities/towns and offers picks like "City, ST"
 */
export function LocationAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "City or ZIP",
  className,
  inputClassName,
}: {
  value: string;
  onChange: (v: string) => void;
  onSelect?: (suggestion: LocationSuggestion) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
}) {
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handle = (val: string) => {
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = val.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const items = await fetchLocationSuggestions(trimmed);
        setSuggestions(items);
        setOpen(items.length > 0);
      } catch {}
      setLoading(false);
    }, 250);
  };

  return (
    <div className={`relative ${className ?? ""}`} ref={wrapRef}>
      <Input
        value={value}
        onChange={(e) => handle(e.target.value)}
        onFocus={() => suggestions.length && setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
        className={inputClassName ?? "border-0 shadow-none h-9 px-0 focus-visible:ring-0"}
      />
      {loading && (
        <Loader2 className="h-3.5 w-3.5 animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
      )}
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full max-h-64 overflow-auto border border-border bg-popover shadow-md left-0">
          {suggestions.map((s) => (
            <li key={s.label}>
              <button
                type="button"
                onClick={() => {
                  onChange(s.label);
                  onSelect?.(s);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
              >
                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                <span>{s.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
